import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import ky from "ky";
import { Loader2, Upload } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRequest } from "@/hooks/use-request";
import { requireAuth } from "@/middleware/auth";
import { useSuggestSubmitMutation } from "@/queries/suggest";

const formSchema = z.object({
	name: z.string().min(1, { message: "제목을 입력해주세요." }),
	content: z.string().min(1, { message: "내용을 입력해주세요." }),
	file_url: z.string().min(1, { message: "파일을 선택해주세요." }),
});

export const Route = createFileRoute("/support/submit")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutationFn } = useSuggestSubmitMutation();
	const { get } = useRequest();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
			content: "",
			file_url: "",
		},
	});

	const { mutate, isPending } = useMutation({
		mutationFn,
		onSuccess: () => {
			toast.success("제안이 성공적으로 제출되었습니다.");
			queryClient.invalidateQueries({ queryKey: ["suggests"] });
			navigate({ to: "/support" });
		},
		onError: () => {
			toast.error("제안 제출에 실패했습니다. 다시 시도해주세요.");
		},
	});

	const handleFileUpload = async (file: File) => {
		try {
			// 파일 정보를 파라미터로 설정
			const params = {
				content_type: file.type,
				content_length: file.size,
				file_name: file.name,
				upload_type: "suggest",
			};

			// 파일 업로드 정보를 직접 요청
			const queryParams = new URLSearchParams({
				content_type: params.content_type,
				content_length: params.content_length.toString(),
				file_name: params.file_name,
				upload_type: params.upload_type,
			});

			const fileResponse = await get(`file?${queryParams.toString()}`);
			const fileData = (
				fileResponse as {
					data: {
						upload_method: string;
						upload_url: string;
						access_url: string;
					};
				}
			).data;

			if (!fileData) {
				toast.error("파일 업로드 정보를 가져올 수 없습니다.");
				return;
			}

			// upload_method에 따라 동적으로 HTTP 메서드 결정
			const method = fileData.upload_method.toUpperCase();
			console.log(file);
			if (method === "POST") {
				await ky.post(fileData.upload_url, {
					body: file,
				});
			} else if (method === "PUT") {
				console.log(fileData.upload_url);
				await ky.put(fileData.upload_url, {
					body: file,
				});
			} else {
				throw new Error(`지원하지 않는 업로드 메서드: ${method}`);
			}

			// access_url을 form의 file_url에 설정
			form.setValue("file_url", fileData.access_url);
			toast.success("파일이 성공적으로 업로드되었습니다.");
		} catch (error) {
			console.error("File upload error:", error);
			toast.error("파일 업로드에 실패했습니다. 다시 시도해주세요.");
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			handleFileUpload(file);
		}
	};

	function onSubmit(values: z.infer<typeof formSchema>) {
		mutate({
			name: values.name,
			content: values.content,
			file_url: values.file_url,
		});
	}

	return (
		<div className="container mx-auto p-4">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="flex items-center gap-0">
									제목<sup className="text-red-500">*</sup>
								</FormLabel>
								<FormControl>
									<Input placeholder="문의 제목을 입력해주세요" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="content"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="flex items-center gap-0">
									내용<sup className="text-red-500">*</sup>
								</FormLabel>
								<FormControl>
									<Textarea
										placeholder="문의 내용을 상세히 입력해주세요"
										className="min-h-32 resize-none bg-white"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="file_url"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="flex items-center gap-0">
									파일<sup className="text-red-500">*</sup>
								</FormLabel>
								<FormControl>
									<div className="flex items-center gap-2">
										<Input
											type="file"
											onChange={handleFileChange}
											className="flex-1"
										/>
										{field.value && (
											<div className="flex items-center gap-2 text-sm text-green-600">
												<Upload className="size-4" />
												업로드 완료
											</div>
										)}
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex justify-end gap-3 pt-4">
						<Link to="/support">
							<Button type="button" variant="outline">
								취소
							</Button>
						</Link>
						<Button
							type="submit"
							disabled={isPending}
							className="flex items-center gap-2"
						>
							{isPending ? (
								<span>
									<Loader2 className="size-4 animate-spin" />
									제출 중...
								</span>
							) : (
								<span>제출</span>
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
